/**
 * PullToRefresh — Feb 2026
 *
 * Native-style pull-to-refresh gesture for mobile. Wraps the main content
 * area inside each role layout. On mobile only — desktop unchanged via
 * `lg:contents` (component contributes no box on desktop).
 *
 * Behaviour:
 *   - Trigger only when window scroll is at top (scrollY === 0).
 *   - Pull > 60 px on release → call `onRefresh` (default: window.location.reload()).
 *   - Spinner appears in the pulled-down area, centered, blue-700, animate-spin.
 *   - Does NOT trigger when a modal/dialog is open (body has overflow:hidden
 *     or a [role="dialog"] is present).
 *   - Pure touch events — no external library.
 */
import React, { useState, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';

const THRESHOLD = 60;
const MAX_PULL = 120;

function isModalOpen() {
  try {
    if (document.body.style.overflow === 'hidden') return true;
    if (document.querySelector('[role="dialog"]')) return true;
    if (document.querySelector('[data-state="open"][role="dialog"]')) return true;
  } catch (_e) {
    // ignore
  }
  return false;
}

export default function PullToRefresh({
  children,
  onRefresh,
  disabled = false,
}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const armed = useRef(false);

  const triggerRefresh = useCallback(() => {
    if (typeof onRefresh === 'function') {
      const maybePromise = onRefresh();
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.finally(() => {
          setRefreshing(false);
          setPullDistance(0);
        });
        return;
      }
    } else {
      // Default: full page reload re-runs all on-mount data fetches
      window.location.reload();
      return; // page will unload
    }
    setTimeout(() => {
      setRefreshing(false);
      setPullDistance(0);
    }, 600);
  }, [onRefresh]);

  const handleTouchStart = (e) => {
    if (disabled || refreshing) return;
    if (isModalOpen()) {
      armed.current = false;
      return;
    }
    if (window.scrollY > 0) {
      armed.current = false;
      return;
    }
    armed.current = true;
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (!armed.current || refreshing) return;
    if (startY.current === null) return;
    if (window.scrollY > 0) {
      setPullDistance(0);
      armed.current = false;
      return;
    }
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0) {
      // Dampen the pull so it feels native
      const damped = Math.min(MAX_PULL, delta * 0.55);
      setPullDistance(damped);
    } else {
      setPullDistance(0);
    }
  };

  const handleTouchEnd = () => {
    if (!armed.current || refreshing) {
      setPullDistance(0);
      startY.current = null;
      armed.current = false;
      return;
    }
    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      setPullDistance(THRESHOLD);
      triggerRefresh();
    } else {
      setPullDistance(0);
    }
    startY.current = null;
    armed.current = false;
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      className="lg:contents"
      data-testid="pull-to-refresh"
    >
      {/* Spinner area — only renders on mobile via lg:hidden */}
      {(pullDistance > 0 || refreshing) && (
        <div
          className="lg:hidden flex items-center justify-center overflow-hidden transition-[height] duration-150 ease-out"
          style={{ height: `${pullDistance}px` }}
          data-testid="pull-to-refresh-spinner"
        >
          <Loader2
            className="w-6 h-6 animate-spin"
            style={{
              color: '#1d4ed8',
              opacity: Math.min(1, pullDistance / THRESHOLD),
            }}
          />
        </div>
      )}
      {children}
    </div>
  );
}
